import { skipToken } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { AlertCircle, Download, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import * as XLSX from "xlsx"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { DocumentList } from "@/components/documents/document-list"
import { EstateRelationshipTree } from "@/components/estate-relationship-tree"
import { UploadDocumentModal } from "@/components/modals/upload-document-modal"
import { Page } from "@/components/page"
import { RecommendationsSection } from "@/components/recommendations-section"
import { RefineReportsSection } from "@/components/refine-reports-section"
import {
  type WillInfographic,
  WillReportContent,
  WillReportPowerPointButton,
} from "@/components/reports/will-report-content"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import type { documentTypeEnum } from "../../../../../../../../api/db/schema/document"
import { ReportTypes } from "../../../../../../../../api/report-generation/report-types"

type DocumentType = (typeof documentTypeEnum.enumValues)[number]

export const Route = createFileRoute("/_authed/_professional/professional/reports/will/$estateId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const { t } = useTranslation()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [additionalPrompt, setAdditionalPrompt] = useState("")

  // Last Will & Testament enum value
  const willDocumentType: DocumentType = "LAST_WILL_AND_TESTAMENT"

  // Fetch WILL_REVIEW reports for this estate
  const { data: willReports = [] } = trpc.reports.getOfType.useQuery({
    estateId,
    reportTypes: ["WILL_REVIEW"],
  })
  const { data: willInfographic } = trpc.reports.getWillInfographic.useQuery(
    estateId ? { estateId } : skipToken,
  )
  const latestWillReport = willReports[0] ?? null

  // Generate will report mutation
  const queryUtils = trpc.useUtils()
  const generateWillReportMutation = trpc.reports.generate.useMutation({
    onSuccess: () => {
      toast.success(t("pages.reports.will.generationStarted"), {
        description: t("pages.reports.estate.generateReports.emailNoticeReport"),
        duration: 8000,
      })
      // Invalidate queries to refresh data when report completes
      queryUtils.reports.getOfType.invalidate({ estateId, reportTypes: ["WILL_REVIEW"] })
      queryUtils.reports.getWillInfographic.invalidate({ estateId })
    },
    onError: (error) => {
      toast.error(t("pages.reports.will.generationError", { message: error.message }))
    },
  })

  return (
    <Page
      showMobileNavigation={false}
      headerSlot={
        <ClientHeader
          estateId={estateId}
          mobileNavigationSlot={
            <SidebarTrigger className="size-10 shrink-0 md:hidden" aria-label="Open navigation" />
          }
        />
      }
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <RecommendationsSection
          title="Last Will & Testament Recommendations"
          headerAction={
            <div className="flex items-center gap-2">
              <DownloadWillDataExcel infographicData={willInfographic} />
              <WillReportPowerPointButton
                report={latestWillReport}
                infographicData={willInfographic}
              />
            </div>
          }
          refineSection={
            <RefineReportsSection>
              <div className="space-y-4">
                {/* Document List */}
                <DocumentList
                  estateId={estateId}
                  documentType={willDocumentType}
                  onDocumentDeleted={() => {
                    queryUtils.documents.listByType.invalidate()
                  }}
                />

                {/* Add Document Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-auto"
                  onClick={() => setUploadModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add document
                </Button>

                {/* Additional Prompt */}
                <div className="space-y-2">
                  <Label htmlFor="additionalPrompt">
                    Prompt agent when generating or rerunning the report (optional)
                  </Label>
                  <Textarea
                    id="additionalPrompt"
                    placeholder="Add specific instructions for the AI analysis..."
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                    className="min-h-[100px] bg-white"
                  />
                </div>

                {/* Run Report Button */}
                <Button
                  type="button"
                  onClick={() => {
                    const prompt = additionalPrompt.trim() || undefined
                    toast.info(
                      prompt
                        ? `Sending report with prompt: "${prompt.slice(0, 50)}${prompt.length > 50 ? "..." : ""}"`
                        : "Sending report without additional prompt",
                    )
                    generateWillReportMutation.mutate({
                      estateId,
                      reportType: [ReportTypes.WILL_REVIEW],
                      additionalPrompt: prompt,
                    })
                  }}
                  className="w-full"
                  disabled={generateWillReportMutation.isPending}
                >
                  {generateWillReportMutation.isPending ? "Generating..." : "Run Report"}
                </Button>
                <Typography as="p" className="text-xs text-muted-foreground mt-3">
                  * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                  insight for each client, saving hours of work. Due to the complex nature of the
                  report generation process you can expect each report to take between 10-20 minutes
                  as our agents evaluate the latest estate laws, user goals, and user data to
                  deliver a truly bespoke output for your clients and their families.
                </Typography>
              </div>
            </RefineReportsSection>
          }
          middleSection={<EstateRelationshipTree estateId={estateId} />}
        >
          {/* Report Insights Section - Conditional rendering based on report existence */}
          {willReports.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                  No Will Reports Generated
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" className="text-muted-foreground">
                  Generate a will report to view insights and recommendations for this estate's last
                  will and testament.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <WillReportContent
              report={latestWillReport}
              infographicData={willInfographic}
              showRecommendations={false}
              showPowerPoint={false}
            />
          )}
        </RecommendationsSection>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        estateId={estateId}
        documentType={willDocumentType}
        onSuccess={() => {
          queryUtils.documents.listByType.invalidate()
        }}
      />
    </Page>
  )
}

type WillInfographicForExcel = NonNullable<WillInfographic>

function DownloadWillDataExcel({
  infographicData,
}: {
  infographicData: WillInfographicForExcel | null | undefined
}) {
  function handleDownload() {
    if (!infographicData?.header) {
      toast.error("No will report data available to export")
      return
    }

    const workbook = XLSX.utils.book_new()

    const assets = infographicData.estateAssets ?? []
    const beneficiaries = infographicData.beneficiaries ?? []
    const stakeholders = infographicData.keyStakeholders ?? []
    const trusts = infographicData.trusts ?? []
    const recommendations = infographicData.recommendations ?? []
    const gaps = infographicData.gapsIdentified ?? []

    const summaryRows = [
      ["Testator", infographicData.header.testatorName ?? ""],
      ["Report date", infographicData.header.reportDate ?? ""],
      ["Assets", assets.length],
      ["Beneficiaries", beneficiaries.length],
      ["Trusts", trusts.length],
      ["Stakeholders", stakeholders.length],
      ["Recommendations", recommendations.length],
      ["Gaps identified", gaps.length],
    ]

    const summarySheet = XLSX.utils.aoa_to_sheet([["Field", "Value"], ...summaryRows])
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary")

    const assetsSheet = XLSX.utils.json_to_sheet(
      assets.map((asset) => ({
        Name: asset.name,
        Type: asset.assetType ?? "",
        Ownership: asset.ownership ?? "",
        Value: asset.value ?? "",
        "Bequest recipient": asset.bequestRecipient ?? "",
        Conditions: asset.conditions ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, assetsSheet, "Assets")

    const beneficiariesSheet = XLSX.utils.json_to_sheet(
      beneficiaries.map((beneficiary) => ({
        Name: beneficiary.name,
        Relationship: beneficiary.relationship ?? "",
        "Share %": beneficiary.sharePercentage ?? "",
        Status: beneficiary.status ?? "",
        Age: beneficiary.age ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, beneficiariesSheet, "Beneficiaries")

    const stakeholdersSheet = XLSX.utils.json_to_sheet(
      stakeholders.map((stakeholder) => ({
        Role: stakeholder.roleLabel ?? stakeholder.role,
        Name: stakeholder.name ?? "",
        Position: stakeholder.position ?? "",
        Status: stakeholder.isMissing ? "Missing" : "Assigned",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, stakeholdersSheet, "Key Roles")

    const trustsSheet = XLSX.utils.json_to_sheet(
      trusts.map((trust) => ({
        Name: trust.name,
        Type: trust.trustTypeLabel ?? trust.trustType ?? "",
        Trustee: trust.trustee ?? "",
        "Successor trustee": trust.successorTrustee ?? "",
        Beneficiaries:
          trust.beneficiaries
            ?.map((beneficiary) =>
              typeof beneficiary === "string" ? beneficiary : beneficiary.name,
            )
            .join(", ") ?? "",
        Distribution: trust.distribution ?? "",
        Conditions: trust.conditions?.join(", ") ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, trustsSheet, "Trusts")

    const recommendationsSheet = XLSX.utils.json_to_sheet(
      recommendations.map((recommendation) => ({
        Order: recommendation.order ?? "",
        Priority: recommendation.priority ?? "",
        Action: recommendation.action,
        Details: recommendation.details ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, recommendationsSheet, "Recommendations")

    const gapsSheet = XLSX.utils.json_to_sheet(
      gaps.map((gap) => ({
        Severity: gap.severity ?? "",
        Issue: gap.issue,
        Details: gap.details ?? "",
      })),
    )
    XLSX.utils.book_append_sheet(workbook, gapsSheet, "Gaps")

    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      if (!sheet?.["!ref"]) continue

      const range = XLSX.utils.decode_range(sheet["!ref"])

      sheet["!cols"] = Array.from({ length: range.e.c + 1 }).map((_, colIndex) => {
        let maxLength = 12

        for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
          const cell = sheet[cellAddress]
          const value = cell?.v ? String(cell.v) : ""
          maxLength = Math.max(maxLength, Math.min(value.length + 2, 42))
        }

        return { wch: maxLength }
      })
    }

    const testatorName = infographicData.header.testatorName ?? "Estate"
    const safeName = testatorName.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_")

    XLSX.writeFile(workbook, `Will_Report_Data_${safeName}.xlsx`)
    toast.success("Excel downloaded")
  }

  return (
    <Button type="button" variant="outline" onClick={handleDownload} disabled={!infographicData}>
      <Download className="h-4 w-4 mr-1" />
      Download Excel
    </Button>
  )
}
